import { useState, useEffect } from 'react';
import Modal from './Modal';
import { useStats } from '../../contexts/StatsContext';
import { useGame } from '../../contexts/GameContext';
import { generateShareText, copyToClipboard } from '../../utils/share';
import './StatsModal.css';

function StatsModal({ isOpen, onClose }) {
  const { stats, getWinPercentage } = useStats();
  const { state } = useGame();
  const [showCopied, setShowCopied] = useState(false);

  const maxCount = Math.max(...Object.values(stats.guessDistribution), 1);

  // Load AdSense ads when modal opens and game is finished
  useEffect(() => {
    if (isOpen && state.gameState !== 'playing') {
      // Wait a bit for modal to be fully rendered
      const timer = setTimeout(() => {
        try {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        } catch (error) {
          // Silently handle - AdSense might not serve ads on localhost
          if (error.message && !error.message.includes('availableWidth')) {
            console.error('AdSense error:', error);
          }
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isOpen, state.gameState]);

  const handleShare = async () => {
    const { gameNumber, guesses, evaluations, gameState } = state;

    if (gameState === 'playing') return;

    const won = gameState === 'won';
    const shareText = generateShareText(gameNumber, evaluations, won, guesses.length);

    // Check if mobile device
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    try {
      // Only use native share on mobile devices
      if (isMobile && navigator.share) {
        await navigator.share({ text: shareText });
      } else {
        // Desktop: always copy to clipboard
        const success = await copyToClipboard(shareText);
        if (success) {
          setShowCopied(true);
          setTimeout(() => setShowCopied(false), 2000);
        }
      }
    } catch (error) {
      // User cancelled share or other error - try clipboard as fallback
      const success = await copyToClipboard(shareText);
      if (success) {
        setShowCopied(true);
        setTimeout(() => setShowCopied(false), 2000);
      }
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Statistics">
      <div className="stats-content">
        <div className="stats-summary">
          <div className="stat-item">
            <div className="stat-value">{stats.gamesPlayed}</div>
            <div className="stat-label">Played</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">{getWinPercentage()}</div>
            <div className="stat-label">Win %</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">{stats.currentStreak}</div>
            <div className="stat-label">Current Streak</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">{stats.maxStreak}</div>
            <div className="stat-label">Max Streak</div>
          </div>
        </div>

        <div className="guess-distribution">
          <h3>Guess Distribution</h3>
          {[1, 2, 3, 4, 5, 6, 7].map((guessNum) => {
            const count = stats.guessDistribution[guessNum] || 0;
            return (
              <div key={guessNum} className="distribution-row">
                <div className="guess-number">{guessNum}</div>
                <div className="distribution-bar-container">
                  <div
                    className={`distribution-bar ${count > 0 ? 'has-value' : ''}`}
                    style={{ width: `${Math.max(7, (count / maxCount) * 100)}%` }}
                  >
                    <span className="bar-value">{count}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {state.gameState !== 'playing' && (
          <>
            {/* Show correct answer if user lost */}
            {state.gameState === 'lost' && state.targetWord && (
              <div className="correct-answer">
                <div className="correct-answer-label">The word was:</div>
                <div className="correct-answer-word">{state.targetWord}</div>
              </div>
            )}

            <div className="stats-actions">
              <button className="share-button" onClick={handleShare}>
                {showCopied ? 'Copied! ✓' : 'Share Results'}
              </button>
            </div>

            {/* AdSense Ad */}
            <div className="stats-ad">
              <ins className="adsbygoogle"
                   data-ad-client="ca-pub-6299919744137808"
                   data-ad-format="auto"
                   data-full-width-responsive="true"></ins>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

export default StatsModal;